import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { CATEGORY_META, CATEGORY_ORDER } from "@/lib/i18n";
import { PlusCircle, LogOut, Star, Eye, EyeOff, Trash2, Pencil, Search, LayoutDashboard, FileText, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";

function StatCard({ label, value, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={testId}>
      <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-slate-500">{label}</div>
      <div className="mt-1 font-display font-bold text-2xl text-slate-900 font-mono-tabular">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [busy, setBusy] = useState(true);

  const load = async () => {
    setBusy(true);
    const qs = new URLSearchParams({ limit: "100" });
    if (q) qs.set("q", q);
    if (cat) qs.set("category", cat);
    const [p, s] = await Promise.all([
      api.get(`/admin/posts?${qs}`),
      api.get(`/admin/stats`),
    ]);
    setPosts(p.data.items);
    setStats(s.data);
    setBusy(false);
  };

  const [showCP, setShowCP] = useState(false);

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user, cat]);

  if (loading) return <div className="p-10">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const togglePublish = async (id) => {
    await api.patch(`/admin/posts/${id}/publish`);
    toast.success("Publish status updated");
    load();
  };
  const toggleFeature = async (id) => {
    await api.patch(`/admin/posts/${id}/feature`);
    toast.success("Featured status updated");
    load();
  };
  const remove = async (id) => {
    if (!confirm("Delete this post permanently?")) return;
    await api.delete(`/admin/posts/${id}`);
    toast.success("Post deleted");
    load();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <Logo size="sm" />
            <span className="text-xs px-2 py-0.5 rounded-sm bg-slate-900 text-white uppercase tracking-wider font-bold">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="admin-view-site" className="text-xs text-slate-600 hover:text-slate-900 hidden sm:inline">View Site</Link>
            <button
              data-testid="admin-change-password"
              onClick={() => setShowCP(true)}
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-100"
            >
              <KeyRound size={13} /> Change Password
            </button>
            <button
              data-testid="admin-logout"
              onClick={() => { logout(); nav("/"); }}
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-100"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
              <LayoutDashboard size={20} /> Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage all notifications, results and updates.</p>
          </div>
          <Link
            to="/admin/posts/new"
            data-testid="admin-new-post-btn"
            className="inline-flex items-center gap-2 h-10 px-4 bg-slate-900 text-white rounded-md text-sm font-semibold hover:bg-slate-800 transition-colors"
          >
            <PlusCircle size={16} /> New Post
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-6">
          <StatCard label="Total Posts" value={stats.total || 0} testId="stat-total" />
          <StatCard label="Published" value={stats.published || 0} testId="stat-published" />
          <StatCard label="Featured" value={stats.featured || 0} testId="stat-featured" />
          <StatCard label="Jobs" value={stats.jobs || 0} testId="stat-jobs" />
          <StatCard label="Results" value={stats.results || 0} testId="stat-results" />
        </div>

        <div className="bg-white border border-slate-200 rounded-md">
          <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
              <input
                data-testid="admin-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                placeholder="Search posts by title, organization…"
                className="w-full h-9 pl-9 pr-3 border border-slate-300 rounded-sm text-sm"
              />
            </div>
            <select
              data-testid="admin-filter-cat"
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              className="h-9 border border-slate-300 rounded-sm text-sm px-2 bg-white"
            >
              <option value="">All categories</option>
              {CATEGORY_ORDER.map((c) => (
                <option key={c} value={c}>{CATEGORY_META[c].en}</option>
              ))}
            </select>
            <button
              onClick={load}
              data-testid="admin-apply-filter"
              className="h-9 px-3 border border-slate-300 rounded-sm text-sm hover:bg-slate-100"
            >Apply</button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-posts-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">Title</th>
                  <th className="px-4 py-2.5 font-semibold">Category</th>
                  <th className="px-4 py-2.5 font-semibold">Last Date</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {busy && (
                  <tr><td colSpan="5" className="p-8 text-center text-slate-500">Loading…</td></tr>
                )}
                {!busy && posts.length === 0 && (
                  <tr><td colSpan="5" className="p-8 text-center text-slate-500">No posts yet.</td></tr>
                )}
                {posts.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`admin-row-${p.slug}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <FileText size={14} className="mt-0.5 flex-shrink-0 text-slate-400" />
                        <div>
                          <div className="font-medium text-slate-900 line-clamp-1">{p.title_en}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5">{p.organization || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 border border-slate-200 rounded-sm bg-white">
                        {CATEGORY_META[p.category]?.short_en || p.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono-tabular text-[13px] text-slate-700">
                      {p.application_last_date || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-sm border ${
                        p.is_published ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-600"
                      }`}>
                        {p.is_published ? "Live" : "Draft"}
                      </span>
                      {p.is_featured && <span className="ml-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm border border-orange-200 bg-orange-50 text-orange-700">Featured</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button data-testid={`toggle-feature-${p.slug}`} onClick={() => toggleFeature(p.id)} title="Toggle featured"
                          className="p-1.5 border border-slate-200 rounded-sm hover:bg-slate-100">
                          <Star size={13} className={p.is_featured ? "fill-orange-500 text-orange-500" : "text-slate-500"} />
                        </button>
                        <button data-testid={`toggle-publish-${p.slug}`} onClick={() => togglePublish(p.id)} title="Toggle publish"
                          className="p-1.5 border border-slate-200 rounded-sm hover:bg-slate-100">
                          {p.is_published ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                        <Link data-testid={`edit-${p.slug}`} to={`/admin/posts/${p.id}`} className="p-1.5 border border-slate-200 rounded-sm hover:bg-slate-100">
                          <Pencil size={13} />
                        </Link>
                        <button data-testid={`delete-${p.slug}`} onClick={() => remove(p.id)} className="p-1.5 border border-red-200 text-red-600 rounded-sm hover:bg-red-50">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <ChangePasswordDialog open={showCP} onOpenChange={setShowCP} />
    </div>
  );
}
